import { createListenerMiddleware, createSlice, isAnyOf, PayloadAction } from '@reduxjs/toolkit';
import { deepEqual } from 'fast-equals';
import { saveStorageValue } from 'hooks/use-storage/use-storage.utils';
import { ChatContextValue } from 'store/iq/chat-context.slice';
import { createInitialChatStore } from 'store/iq/chat-store/chat-store.slice';
import { ChatStoreInit } from 'store/iq/chat-store/chat-store.types';
import { NewHelpfulMessageAction, ToggleHelpfulAction, ToggleIncludeInPromptAction } from 'store/iq/helpful-message.slice';
import { queryResultName } from 'store/query-result.slice';
import { RootState } from 'store/root.types';
import { MessageWithId } from 'types/chat-messages';
import { ChatHistoryLocalStorageValue, ChatHistoryStoreValue } from './chat-history-store.types';

const initialState: ChatHistoryStoreValue = {
  storageKey: '',
  state: {
    selectedChatIdx: 0,
  },
  chatStores: [],
  helpfulMessages: [],
};

export const chatHistoryStoreSliceName = 'chatHistoryStore';

export const chatHistoryStoreSlice = createSlice({
  name: chatHistoryStoreSliceName,
  initialState,
  reducers: {
    initializeEmptyChatHistoryStore: (state, action: PayloadAction<{ storageKey: string; newChatStore: ChatStoreInit }>) => {
      state.state = initialState.state;
      state.helpfulMessages = initialState.helpfulMessages;
      state.chatStores = [action.payload.newChatStore];
      state.storageKey = action.payload.storageKey;
    },
    initiliazeChatHistoryStore: (state, action: PayloadAction<ChatHistoryStoreValue>) => {
      state.state.selectedChatIdx = action.payload.state.selectedChatIdx;
      state.helpfulMessages = action.payload.helpfulMessages;
      state.chatStores = action.payload.chatStores;
    },
    initializeChatHistoryStateFromStorage: (state, action: PayloadAction<ChatHistoryLocalStorageValue | null>) => {
      if (!action.payload) {
        return;
      }
      state.state.selectedChatIdx = action.payload.state.selectedChatIdx;
      state.helpfulMessages = action.payload.helpfulMessagesInit?.value || [];
      state.chatStores = action.payload.chatStoresInits.map((chatStoreInit) => ({
        ...chatStoreInit,
      }));
      state.storageKey = action.payload.storageKey;
    },
    updateChatHistoryStore: (state, action: PayloadAction<ChatHistoryStoreValue>) => {
      state.state.selectedChatIdx = action.payload.state.selectedChatIdx;
      state.helpfulMessages = action.payload.helpfulMessages;
      state.chatStores = action.payload.chatStores;
    },
    // sets the first user message for the current chat
    setFirstUserMessageForHistory: (state, action: PayloadAction<{ message: string; storageKey: string }>) => {
      const selectedChatStore = state.chatStores[state.state.selectedChatIdx];
      const messages = selectedChatStore?.messages;
      const messageValues = messages?.value;
      if (messageValues) {
        state.chatStores = state.chatStores.map((chatStore, index) => {
          if (index === state.state.selectedChatIdx && messages) {
            return {
              ...chatStore,
              messages: {
                ...messages,
                state: {
                  ...messages.state,
                  firstUserMessage: action.payload.message,
                },
              },
            };
          }
          return chatStore;
        });
      }
      state.storageKey = action.payload.storageKey;
    },
    // sets the upcoming assistant message index for the current chat
    setUpcomingAssistantMsgIdxForHistory: (state, action: PayloadAction<{ newIdx: number; storageKey: string }>) => {
      const selectedChatStore = state.chatStores[state.state.selectedChatIdx];
      const messages = selectedChatStore?.messages;
      const messageValues = messages?.value;

      if (messageValues) {
        state.chatStores = state.chatStores.map((chatStore, index) => {
          if (index === state.state.selectedChatIdx && messages) {
            return {
              ...chatStore,
              messages: {
                ...messages,
                state: {
                  ...messages.state,
                  upcomingAssistantMsgIdx: action.payload.newIdx,
                },
              },
            };
          }
          return chatStore;
        });
      }
      state.storageKey = action.payload.storageKey;
    },
    // updates the messages and the upcoming assistant message index for the current chat
    updateMessagesAndIdxForHistory: (state, action: PayloadAction<{ index: number; value: MessageWithId[][]; storageKey: string }>) => {
      const selectedChatStore = state.chatStores[state.state.selectedChatIdx];
      const messages = selectedChatStore?.messages;
      const messageValues = messages?.value;

      if (messageValues) {
        const updatedMessages = action.payload.value;
        state.chatStores = state.chatStores.map((chatStore, index) => {
          if (index === state.state.selectedChatIdx && messages) {
            return {
              ...chatStore,
              messages: {
                ...messages,
                value: updatedMessages,
                state: {
                  ...messages.state,
                  upcomingAssistantMsgIdx: action.payload.index,
                },
              },
            };
          }
          return chatStore;
        });
      }
      state.storageKey = action.payload.storageKey;
    },
    updateChatContextForHistory: (state, action: PayloadAction<{ ctx: ChatContextValue; storageKey: string }>) => {
      const currentChatStore = state.chatStores[state.state.selectedChatIdx];
      if (currentChatStore && (action.payload.ctx.bucket || action.payload.ctx.scope || action.payload.ctx.collections.length)) {
        currentChatStore.context.value = action.payload.ctx;
      }
      state.storageKey = action.payload.storageKey;
    },
    updateQueryForHistory: (state, action: PayloadAction<{ newQuery: string; storageKey: string }>) => {
      const { selectedChatIdx } = state.state;
      if (selectedChatIdx !== undefined && state.chatStores[selectedChatIdx] && state.chatStores[selectedChatIdx].state) {
        state.chatStores[selectedChatIdx].state.query = action.payload.newQuery;
      }
      state.storageKey = action.payload.storageKey;
    },
    setSelectedChatIdx: (state, action: PayloadAction<number>) => {
      state.state.selectedChatIdx = action.payload;
    },
    addChatStore: (state, action: PayloadAction<ChatStoreInit>) => {
      state.chatStores.push(action.payload);
      state.state.selectedChatIdx = state.chatStores.length - 1;
    },
    removeChatStore: (state, action: PayloadAction<number>) => {
      state.chatStores.splice(action.payload, 1);
    },
    resetChatStores: (state, action: PayloadAction<ChatStoreInit>) => {
      state.chatStores = [];
      state.chatStores.push(action.payload);
    },
    newHelpfulMessageForHistory: (state, action: PayloadAction<{ message: NewHelpfulMessageAction['message']; storageKey: string }>) => {
      state.helpfulMessages.push({
        ...action.payload.message,
        isMarkedAsHelpful: true,
        queryHasBeenRunAndIsNotFatal: true,
        isIncludedInThePrompt: !state.helpfulMessages.find((message) => deepEqual(message.context, action.payload.message.context)),
        type: 'helpfulMessage',
      });
      state.storageKey = action.payload.storageKey;
    },
    toggleHelpfulMessageForHistory: (state, action: PayloadAction<{ message: ToggleHelpfulAction['message']; storageKey: string }>) => {
      const helpfulMessageToUpdate = state.helpfulMessages.find(
        (message) =>
          ('id' in action.payload.message && message.originalMessageId === action.payload.message.id) ||
          message.originalMessageId === action.payload.message.originalMessageId
      );
      if (helpfulMessageToUpdate) {
        if (helpfulMessageToUpdate.isMarkedAsHelpful) {
          state.helpfulMessages = state.helpfulMessages.filter(
            (message) => message.originalMessageId !== helpfulMessageToUpdate.originalMessageId
          );
          return state;
        }

        helpfulMessageToUpdate.isMarkedAsHelpful = !helpfulMessageToUpdate.isMarkedAsHelpful;
      }
      state.storageKey = action.payload.storageKey;
      return state;
    },
    toggleIncludeInPromptForHistory: (state, action: PayloadAction<{ id: ToggleIncludeInPromptAction['id']; storageKey: string }>) => {
      const message = state.helpfulMessages.find((message) => message.originalMessageId === action.payload.id);
      const currentIsIncludeInPrompt = message?.isIncludedInThePrompt;
      state.helpfulMessages.forEach((message) => {
        message.isIncludedInThePrompt = false;
      });
      if (message) {
        message.isIncludedInThePrompt = !currentIsIncludeInPrompt;
      }
      state.storageKey = action.payload.storageKey;
    },
    nextChat: (state, action: PayloadAction<{ storageKey: string; newChatStore?: ChatStoreInit }>) => {
      if (state.state.selectedChatIdx >= state.chatStores.length - 1 && action.payload.newChatStore) {
        state.chatStores.push(action.payload.newChatStore);
      }
      state.state.selectedChatIdx++;
      state.storageKey = action.payload.storageKey;
    },
    newChat: (state, action: PayloadAction<{ storageKey: string; newChatStore?: ChatStoreInit }>) => {
      if (action.payload.newChatStore) {
        state.state.selectedChatIdx = state.chatStores.length - 1;
        state.chatStores.push(action.payload.newChatStore);
      }
      state.state.selectedChatIdx++;
      state.storageKey = action.payload.storageKey;
    },
    prevChat: (state, action: PayloadAction<{ storageKey: string }>) => {
      if (state.state.selectedChatIdx > 0) {
        state.state.selectedChatIdx--;
      }
      state.storageKey = action.payload.storageKey;
    },
    duplicateAndAppendChat: (state, action: PayloadAction<{ storageKey: string; newChatStore: ChatStoreInit }>) => {
      state.chatStores.push(action.payload.newChatStore);
      state.state.selectedChatIdx = state.chatStores.length - 1;
      state.storageKey = action.payload.storageKey;
    },
    deleteChat: (state, action: PayloadAction<{ storageKey: string; newChatStore?: ChatStoreInit }>) => {
      const idxToDelete = state.state.selectedChatIdx;
      if (idxToDelete === 0 && state.chatStores.length === 1 && action.payload.newChatStore) {
        state.chatStores[idxToDelete] = action.payload.newChatStore;
      } else {
        state.chatStores.splice(idxToDelete, 1);
        if (state.chatStores[idxToDelete + 1]) {
          state.state.selectedChatIdx = idxToDelete;
        } else if (state.chatStores[idxToDelete - 1]) {
          state.state.selectedChatIdx = idxToDelete - 1;
        }
      }
      state.storageKey = action.payload.storageKey;
    },
  },
});

export type ChatHistoryStore = ReturnType<typeof chatHistoryStoreSlice.reducer>;

export const {
  initializeEmptyChatHistoryStore,
  initiliazeChatHistoryStore,
  initializeChatHistoryStateFromStorage,
  setSelectedChatIdx,
  addChatStore,
  setFirstUserMessageForHistory,
  removeChatStore,
  nextChat,
  newChat,
  prevChat,
  duplicateAndAppendChat,
  deleteChat,
  resetChatStores,
  updateChatHistoryStore,
  updateChatContextForHistory,
  updateQueryForHistory,
  updateMessagesAndIdxForHistory,
  setUpcomingAssistantMsgIdxForHistory,
  newHelpfulMessageForHistory,
  toggleHelpfulMessageForHistory,
  toggleIncludeInPromptForHistory,
} = chatHistoryStoreSlice.actions;

export default chatHistoryStoreSlice.reducer;

export const chatHistoryMiddleware = createListenerMiddleware();

chatHistoryMiddleware.startListening({
  matcher: isAnyOf(
    initializeEmptyChatHistoryStore,
    initializeChatHistoryStateFromStorage,
    prevChat,
    nextChat,
    newChat,
    deleteChat,
    duplicateAndAppendChat,
    updateChatContextForHistory,
    updateMessagesAndIdxForHistory,
    updateQueryForHistory,
    setFirstUserMessageForHistory,
    setUpcomingAssistantMsgIdxForHistory,
    newHelpfulMessageForHistory,
    toggleHelpfulMessageForHistory,
    toggleIncludeInPromptForHistory
  ),
  effect: (action, listenerApi) => {
    const chatHistory = (listenerApi.getState() as RootState)[chatHistoryStoreSliceName];
    const queryResults = (listenerApi.getState() as RootState)[queryResultName];
    const dbId = chatHistory.storageKey ? chatHistory.storageKey.split('_')[1] : '';
    const { dontSaveHistory } = queryResults.queryByDatabaseId[dbId]
      ? queryResults.queryByDatabaseId[dbId].options.query
      : { dontSaveHistory: false };

    const chatHistoryForLocalStorage: ChatHistoryLocalStorageValue = dontSaveHistory
      ? {
          state: {
            selectedChatIdx: 0,
          },
          chatStoresInits: [createInitialChatStore()],
          helpfulMessagesInit: { value: [] },
          storageKey: action.payload.storageKey,
        }
      : {
          state: {
            ...chatHistory.state,
            selectedChatIdx: chatHistory.state.selectedChatIdx,
          },
          chatStoresInits: chatHistory.chatStores.map((chatStore) => ({
            ...chatStore,
          })),
          helpfulMessagesInit: { value: chatHistory.helpfulMessages },
          storageKey: action.payload.storageKey,
        };

    if (action.payload.storageKey) {
      saveStorageValue({
        storageKey: chatHistory.storageKey,
        value: chatHistoryForLocalStorage,
      });
    }
  },
});
